import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  AnnouncementsProps, ClockProps, EventItem, CountdownProps, Design, DonationQrProps, DonationsProps, EventsProps, JumuahProps, PostersProps, PrayerTimesProps,
  LogoProps, ScriptureProps, TextOptions, TickerProps, RamadanProps, Widget, WidgetStyle, HeadingProps, ParagraphProps, MakruhProps,
} from '@noorview/core';
import { ACCENTS, ASPECTS, BACKGROUND_SWATCHES, FONT_LIST, PATTERNS, isLightBackground, patternPreviewSvg, type BackgroundPattern, type PatternCoverage, type PatternSize, PRAYER_LABEL, WIDGET_DESIGNS, MAX_RADIUS, canScaleContent, cornerRadius, designOf, designUses, gridMetrics, rectToPx, type Aspect, type FontGroup, MAX_BORDER, MAX_TEXT, MIN_TEXT, POSTER_SHAPES, STYLES, TEXT_SIZES, WIDGETS, autoInk, clampRect, createWidget, hexToRgb, keepShape, lockedRatio, minSize, nearestPosterShape, newId, placeNew, rgbToHex, widgetName, type PosterShape, AYAH_TOPICS, HADITH_BOOKS, HADITH_LIST, QURAN_AYAT, ayahReference, hadithBookName, hadithReference, scriptureFor, ymd, MESSAGE_PRESETS, presetOf, type MessagePreset, addDays, type TimetableDay, PLACEMENTS, type BroadcastPlacement, SLIDE_SPEEDS, type SlideSpeed, LED_COLOURS, LED_DEFAULT, isLed, HEADING_MAX, PARAGRAPH_MAX, PARAGRAPH_TITLE_MAX, MAKRUH_LIMITS, makruhPeriods, indexTimetable, hhmm } from '@noorview/core';
import { screenStatus, STATUS_LABEL, STATUS_PILL, useApp, useScreenBroadcasts } from '../data/store';
import { Icon } from '../ui/icons';
import { useParams } from 'react-router';
import { copySelected } from './clipActions';
import { ColourChoice, Popover, PresetNumber, Segmented, Slider, Stepper, Switch, confirm } from '../ui/controls';
import { ScrollArea } from '../ui/ScrollArea';
import { Select } from '../ui/pickers';
import { AreaField, ListEditor, NumberField, SelectField, TextField, ToggleRow, UploadButton } from '../ui/fields';
import { StageView } from '../ui/StageView';
import { PositionPicker } from '../ui/PositionPicker';
import { selectedWidget, useStudio } from './store';
import { duplicateWidget, sampleTimetable } from './ops';
import { WidgetIssues } from './Contrast';
import { liveDesign } from '../app/designs';
import { openSheet } from '../app/ui';

export function Inspector() {
  const w = useStudio(selectedWidget);
  return (
    <ScrollArea as="section" className="card glass inspector" label="Inspector">
      {w ? <WidgetInspector key={w.id} w={w} /> : <ScreenInspector />}
    </ScrollArea>
  );
}

/** Widgets with a highlight colour, and what it colours. */
const HIGHLIGHT: Partial<Record<Widget['type'], string>> = {
  prayerTimes: 'Next prayer highlight', events: 'Date tiles', ticker: 'Tag colour', donations: 'Progress colour', announcements: 'Page dots',
  heading: 'Accent colour', paragraph: 'Accent colour',
};
const TILE_SWATCHES = ['#FFFFFF', '#0B1220', '#1C2639', '#0A84FF', '#30D158', '#FFD60A', '#FF9F0A'] as const;
const PRAYER_KEYS = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'] as const;

/** Tile colours for all prayers at once, or one prayer at a time. */
function PrayerTileColours({ w, p }: { w: Widget; p: PrayerTimesProps }) {
  const [which, setWhich] = useState<'all' | (typeof PRAYER_KEYS)[number]>('all');
  const set = (patch: Partial<PrayerTimesProps>, key: string) => useStudio.getState().updateWidget(w.id, (x) => ({ props: { ...x.props, ...patch } }), `p:${key}`);
  const own = which === 'all' ? null : p.prayerColors?.[which] ?? {};
  const setOwn = (patch: { tile?: string | null; text?: string | null }, key: string) => {
    if (which === 'all') return;
    const next = { ...(p.prayerColors ?? {}), [which]: { ...(p.prayerColors?.[which] ?? {}), ...patch } };
    set({ prayerColors: next }, `${which}:${key}`);
  };
  const keys = PRAYER_KEYS.filter((k) => k !== 'sunrise' || p.showSunrise);
  return (
    <div className="tile-colours">
      <div className="section-label">Tile colours</div>
      <div className="chips" role="group" aria-label="Which prayer">
        {(['all', ...keys] as const).map((k) => (
          <button key={k} type="button" className={`cat${which === k ? ' on' : ''}`} aria-pressed={which === k} onClick={() => setWhich(k)}>
            {k !== 'all' && p.prayerColors?.[k]?.tile && <i className="chip-sw" style={{ background: p.prayerColors[k]!.tile! }} aria-hidden />}
            {k === 'all' ? 'All prayers' : PRAYER_LABEL[k]}
          </button>
        ))}
      </div>
      <div className="field"><span>{which === 'all' ? 'Tile colour' : `${PRAYER_LABEL[which]} tile`}</span>
        <ColourChoice label="Tile colour" autoLabel={which === 'all' ? 'Style colour (automatic)' : 'Same as the other prayers'} swatches={TILE_SWATCHES}
          value={which === 'all' ? p.tileColor : own?.tile} keyPrefix="tile" onChange={(v, k) => (which === 'all' ? set({ tileColor: v }, `tile${k ?? ''}`) : setOwn({ tile: v }, `tile${k ?? ''}`))} />
      </div>
      <div className="field"><span>{which === 'all' ? 'Text on the tiles' : `${PRAYER_LABEL[which]} text`}</span>
        <ColourChoice label="Tile text colour" autoLabel="Automatic (whichever reads best)" swatches={TEXT_SWATCHES}
          value={which === 'all' ? p.tileText : own?.text} keyPrefix="ttext" onChange={(v, k) => (which === 'all' ? set({ tileText: v }, `ttext${k ?? ''}`) : setOwn({ text: v }, `ttext${k ?? ''}`))} />
      </div>
      {which !== 'all' && (own?.tile || own?.text) && <button type="button" className="link" onClick={() => setOwn({ tile: null, text: null }, 'reset')}>Use the same colours as the other prayers</button>}
      <p className="hint" style={{ margin: 0 }}>The next prayer uses the highlight colour, unless you give it its own colour here: then it keeps your colour with a highlight ring.</p>
    </div>
  );
}

const TEXT_SWATCHES = ['#FFFFFF', '#0B1220', '#FFD60A', '#30D158', '#0A84FF', '#FF9F0A', '#FF453A'] as const;
const BORDER_SWATCHES = ['#FFFFFF', '#0B1220', '#0A84FF', '#30D158', '#FFD60A', '#FF9F0A', '#BF5AF2'] as const;

/* ---------- selected widget ---------- */

function WidgetInspector({ w }: { w: Widget }) {
  const st = useStudio.getState;
  const design = useStudio((s) => s.design)!;
  const up = (patch: Partial<Widget>, key?: string) => st().updateWidget(w.id, patch, key);
  const props = <P,>(patch: Partial<P>, key?: string) => st().updateWidget(w.id, (x) => ({ props: { ...x.props, ...patch } }), key ? `p:${key}` : undefined);
  const def = WIDGETS[w.type];
  const min = minSize(w, design.cols, design.rows);
  const setSize = (nw: number, nh: number, drive: 'w' | 'h') => {
    const r = clampRect({ x: Math.min(w.x, design.cols - nw), y: Math.min(w.y, design.rows - nh), w: nw, h: nh }, design.cols, design.rows, min);
    up(keepShape(w, r, design, drive), 'size');
  };
  const textRef = useRef<HTMLButtonElement>(null);
  const [textOpen, setTextOpen] = useState(false);
  const { screenId } = useParams();
  const screenName = useApp((s) => s.ws?.screens.find((x) => x.id === screenId)?.name) ?? 'this screen';
  const styleLabel = w.type === 'makruh' ? 'Red alert' : STYLES.find((s) => s.value === w.style)?.label ?? 'Glass';
  const weightName = { 400: 'Regular', 500: 'Medium', 600: 'Semibold', 700: 'Bold', 800: 'Heavy' }[w.text.weight];
  const alignName = w.type === 'makruh' ? 'Scrolling' : { left: 'Left', center: 'Centred', right: 'Right' }[w.text.align];
  const bw = w.border?.width ?? 0;
  const hasText = w.type !== 'posters' && w.type !== 'logo';

  const remove = async () => {
    if (w.locked) return;
    st().change((d) => ({ ...d, widgets: d.widgets.filter((x) => x.id !== w.id) }));
    st().select(null);
  };
  const duplicate = () => {
    const copy = duplicateWidget(st().design!, w);
    st().change((x) => ({ ...x, widgets: [...x.widgets, copy] }));
    st().select(copy.id);
  };
  const order = (front: boolean) => st().change((d) => {
    const rest = d.widgets.filter((x) => x.id !== w.id);
    return { ...d, widgets: front ? [...rest, w] : [w, ...rest] };
  });

  return (
    <div className="insp">
      <div className="insp-head">
        <span className="insp-ic"><Icon name={def.icon} size={26} /></span>
        <div className="insp-name"><b>{widgetName(w)}</b><small>{w.w} × {w.h} cells · {styleLabel}{w.locked ? ' · Locked' : ''}</small></div>
        <span className="spacer" />
        <button type="button" className="icon-btn danger" style={{ width: 40, height: 40 }} aria-label={`Delete ${widgetName(w)}`} title={w.locked ? 'Unlock it in Layers to delete' : 'Delete (⌫)'} disabled={w.locked} onClick={remove}><Icon name="trash" size={18} /></button>
      </div>
      <WidgetIssues id={w.id} />
      {WIDGET_DESIGNS[w.type] && <>
        <div className="sep" />
        <DesignPicker w={w} />
      </>}
      <div className="sep" />
      <div className="insp-sec">
        <div className="section-label">Size</div>
        <div className="row"><span className="label">Columns</span><span className="spacer" /><Stepper label="Columns" value={w.w} min={min.w} max={design.cols} onChange={(v) => setSize(v, w.h, 'w')} /></div>
        <div className="row"><span className="label">Rows</span><span className="spacer" /><Stepper label="Rows" value={w.h} min={min.h} max={design.rows} onChange={(v) => setSize(w.w, v, 'h')} /></div>
        {lockedRatio(w) && <p className="hint" style={{ margin: 0 }}>Keeps its {POSTER_SHAPES.find((p) => p.value === (w.props as PostersProps).shape)?.label.toLowerCase()} shape: change one side and the other follows.</p>}
      </div>
      {w.type === 'announcements' && <QuickPosition w={w} />}
      {canScaleContent(w.type, designOf(w)) && <>
        <div className="sep" />
        <ContentSize w={w} />
      </>}
      {w.type === 'makruh' ? <>
        <div className="sep" />
        <p className="hint" style={{ margin: 0 }}>Always solid red with white text, so everyone notices it. You can change its font.</p>
      </> : <>
      <div className="sep" />
      <div className="insp-sec">
        <div className="section-label">Style</div>
        <Segmented size="sm" label="Style" value={w.style} onChange={(v: WidgetStyle) => up({ style: v })} options={STYLES} />
        <CornerRadius w={w} />
      </div>
      <div className="sep" />
      <div className="insp-sec">
        <div className="row"><span className="section-label">Colour</span><span className="spacer" />
          <label className="opacity" title="How solid the widget is">
            <input className="input mono" inputMode="numeric" aria-label="Opacity" value={`${w.opacity}%`}
              onChange={(e) => { const n = parseInt(e.target.value.replace(/\D/g, ''), 10); if (Number.isFinite(n)) up({ opacity: Math.max(0, Math.min(100, n)) }, 'opacity'); }} />
          </label>
        </div>
        {w.style === 'none'
          ? <p className="hint" style={{ margin: 0 }}>This style has no background, so only the content shows. Pick another style to colour the widget.</p>
          : <ColourChoice label="Widget colour" autoLabel={`${styleLabel} colour (automatic)`} swatches={ACCENTS} value={w.fill} keyPrefix="fill"
            onChange={(v, key) => up({ fill: v }, key)} />}
        {HIGHLIGHT[w.type] && designUses(w, 'accent') && <>
          <div className="section-label" style={{ marginTop: 6 }}>{HIGHLIGHT[w.type]}</div>
          <ColourChoice label={HIGHLIGHT[w.type]!} swatches={ACCENTS} value={w.accent} keyPrefix="accent" onChange={(v, key) => up({ accent: v ?? '#0A84FF' }, key)} />
        </>}
      </div>
      <div className="sep" />
      <div className="insp-sec">
        <div className="row"><span className="section-label">Border</span><span className="spacer" /><span className="hint mono">{bw ? `${bw} px` : 'None'}</span></div>
        <Slider label="Border thickness" value={bw} min={0} max={MAX_BORDER} onChange={(v) => up({ border: v ? { width: v, color: w.border?.color ?? '#FFFFFF' } : null }, 'border')}
          format={(v) => (v ? `${v} px` : 'No border')} ends={['None', `${MAX_BORDER} px`]} />
        {bw > 0 && <ColourChoice label="Border colour" swatches={BORDER_SWATCHES} value={w.border?.color ?? '#FFFFFF'} keyPrefix="bc"
          onChange={(v, key) => up({ border: { width: bw, color: v ?? '#FFFFFF' } }, key)} />}
        <p className="hint" style={{ margin: 0 }}>Drawn inside the widget’s edge, so the spacing between widgets stays the same.</p>
      </div>
      </>}
      {hasText && <>
        <div className="sep" />
        <div className="insp-sec">
          <div className="section-label">Text</div>
          <button ref={textRef} type="button" className="text-opt" onClick={() => setTextOpen((v) => !v)} aria-expanded={textOpen}>
            <span className="aa" style={w.textColor ? { background: w.textColor, color: autoInkHex(w.textColor) } : undefined}>Aa</span>
            <span className="tx"><b>{w.text.font}</b><small>{weightName} · {w.fit == null ? `${w.text.size} pt` : `${w.fit}% size`} · {alignName}</small></span>
            <Icon name="chevron" size={16} />
          </button>
          {w.fit == null && <div className="row"><span className="label">Scale text with widget</span><span className="spacer" /><Switch label="Scale text with widget" checked={w.text.scale} onChange={(v) => up({ text: { ...w.text, scale: v } })} /></div>}
          <Popover anchor={textRef.current} open={textOpen} onClose={() => setTextOpen(false)} width={380} align="end" label="Text options">
            <TextOptionsPanel w={w} onClose={() => setTextOpen(false)} />
          </Popover>
        </div>
      </>}
      <div className="sep" />
      <div className="insp-sec">
        <div className="section-label">Content</div>
        <ContentEditor w={w} props={props} />
      </div>
      <div className="sep" />
      <div className="insp-sec">
        <div className="section-label">Arrange</div>
        <div className="arrange">
          <button type="button" className="btn sm plain" onClick={() => order(true)}><Icon name="front" size={16} />To front</button>
          <button type="button" className="btn sm plain" onClick={() => order(false)}><Icon name="backward" size={16} />To back</button>
          <button type="button" className="btn sm plain" onClick={duplicate}><Icon name="copy" size={16} />Duplicate</button>
          <button type="button" className="btn sm plain" onClick={() => copySelected(screenName)} title="Copy it to paste here or on another screen"><Icon name="paste" size={16} />Copy</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Announcements: size and place the widget in one tap (full screen, half, the middle, a band) and bring it to the
 * front, so it sits over the rest of the layout. Moving it back or deleting it leaves everything underneath as it was.
 */
function QuickPosition({ w }: { w: Widget }) {
  const design = useStudio((s) => s.design)!;
  const cells = (b: [number, number, number, number]) => {
    const x = Math.round(b[0] * design.cols), y = Math.round(b[1] * design.rows);
    return { x, y, w: Math.max(1, Math.round((b[0] + b[2]) * design.cols) - x), h: Math.max(1, Math.round((b[1] + b[3]) * design.rows) - y) };
  };
  const cur = PLACEMENTS.find((p) => { const c = cells(p.box); return c.x === w.x && c.y === w.y && c.w === w.w && c.h === w.h; })?.value ?? 'widget';
  const pick = (v: BroadcastPlacement) => {
    const p = PLACEMENTS.find((x) => x.value === v);
    if (!p) return;
    useStudio.getState().change((d) => ({ ...d, widgets: [...d.widgets.filter((x) => x.id !== w.id), { ...w, ...cells(p.box) }] }));
  };
  return (
    <>
      <div className="sep" />
      <div className="insp-sec">
        <div className="section-label">Quick position</div>
        <PositionPicker value={cur} onChange={pick} />
        <p className="hint" style={{ margin: 0 }}>Sizes the widget and brings it to the front, on top of the others.</p>
      </div>
    </>
  );
}

/** Live previews of each design, drawn by the same renderer the screens use. */
function DesignPicker({ w }: { w: Widget }) {
  const ws = useApp((s) => s.ws)!;
  const design = useStudio((s) => s.design)!;
  const list = WIDGET_DESIGNS[w.type]!;
  const cur = designOf(w);
  const m = gridMetrics(design);
  const px = rectToPx(w, m);
  const ratio = px.width / Math.max(1, px.height);
  const aspect: Aspect = ratio > 1.45 ? '16:9' : ratio < 0.75 ? '9:16' : '4:3';
  // The previews show each design at its automatic size, so they don't redraw while the Content size slider (or the
  // widget's position) moves: redrawing all of them on every step made the slider lag.
  const wKey = JSON.stringify({ ...w, fit: null, x: 0, y: 0 });
  const previews = useMemo(() => list.map((d) => {
    const a = ASPECTS[aspect];
    const one: Widget = { ...w, id: `preview-${d.value}`, x: 0, y: 0, w: a.cols, h: a.rows, fit: null, props: { ...w.props, design: d.value } };
    return { d, design: { ...design, aspect, cols: a.cols, rows: a.rows, margin: 0.03, widgets: [one] } as Design };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [wKey, design.background, design.gap, aspect]);
  const today = ymd(new Date(), ws.masjid.timezone);
  const timetable = useMemo(() => {
    const has = ws.timetable.some((d) => d.date >= today && d.date <= addDays(today, 1) && (w.type === 'sehri' ? d.sehri : w.type === 'tarawih' ? d.tarawih : d.fajr));
    return has ? ws.timetable : sampleTimetable(today);
  }, [ws.timetable, today, w.type]);
  return (
    <div className="insp-sec">
      <div className="section-label">Design</div>
      <div className={`designs a-${aspect.replace(':', '-')}`} role="radiogroup" aria-label="Design">
        {previews.map(({ d, design: dd }) => (
          <button key={d.value} type="button" role="radio" aria-checked={cur === d.value} className={`design-opt${cur === d.value ? ' on' : ''}`}
            onClick={() => useStudio.getState().updateWidget(w.id, (x) => ({ props: { ...x.props, design: d.value } }))}>
            <span className="design-thumb"><StageView design={dd} masjid={ws.masjid} timetable={timetable} quality="balanced" frozen className="stage-fill" label={`${d.label} design`} /></span>
            <span className="design-name">{d.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Scales everything inside the widget together, from small up to filling the widget (keeping a small inner margin).
 * Starts from how the widget looks now, so the first nudge doesn't jump.
 */
function ContentSize({ w }: { w: Widget }) {
  const [measured, setMeasured] = useState<number | null>(null);
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(`.editor-stage .nvd-w[data-id="${CSS.escape(w.id)}"]`);
      const v = Number(el?.dataset.fill);
      setMeasured(Number.isFinite(v) && v > 0 ? v : null);
    });
    return () => cancelAnimationFrame(id);
  }, [w]);
  const auto = w.fit == null;
  const value = w.fit ?? measured ?? 80;
  const set = (v: number | null) => useStudio.getState().updateWidget(w.id, { fit: v }, v == null ? undefined : 'fit');
  return (
    <div className="insp-sec">
      <div className="row"><span className="section-label row" style={{ gap: 6 }}><Icon name="scale" size={15} />Content size</span><span className="spacer" />
        {auto ? <span className="hint">Automatic</span> : <button type="button" className="link" onClick={() => set(null)}>Automatic</button>}
      </div>
      <Slider label="Content size" value={Math.round(value)} min={20} max={100} onChange={(v) => set(v)} format={(v) => (v >= 100 ? 'Fills the widget' : `${v}%`)} ends={['Small', 'Fill']} />
      <p className="hint" style={{ margin: 0 }}>Scales everything in the widget together: text, clocks and pictures. At Fill it reaches a small margin inside the edge.</p>
    </div>
  );
}

/** Corner curve, from square to well rounded (capped so a widget never becomes a circle). */
function CornerRadius({ w }: { w: Widget }) {
  const design = useStudio((s) => s.design)!;
  const m = gridMetrics(design);
  const cap = Math.round(Math.min(MAX_RADIUS, Math.min(rectToPx(w, m).width, rectToPx(w, m).height) * 0.22));
  const cur = cornerRadius(w, m);
  const set = (v: number | null) => useStudio.getState().updateWidget(w.id, { radius: v }, v == null ? undefined : 'radius');
  const all = () => useStudio.getState().change((d) => ({ ...d, widgets: d.widgets.map((x) => ({ ...x, radius: w.radius ?? null })) }));
  return (
    <div className="corner">
      <div className="row"><span className="section-label row" style={{ gap: 6 }}><Icon name="radius" size={15} />Corner radius</span><span className="spacer" />
        {w.radius == null ? <span className="hint">Standard</span> : <button type="button" className="link" onClick={() => set(null)}>Standard</button>}</div>
      <Slider label="Corner radius" value={Math.min(cur, cap)} min={0} max={Math.max(1, cap)} onChange={(v) => set(v)} format={(v) => (v === 0 ? 'Square' : `${v} px`)} ends={['Square', 'Round']} />
      {design.widgets.length > 1 && <button type="button" className="link" style={{ alignSelf: 'flex-start', fontSize: 14 }} onClick={all}>Use this curve on every widget</button>}
    </div>
  );
}

const autoInkHex = (hex: string) => rgbToHex(autoInk(hexToRgb(hex)));

/* ---------- Font picker: every font shown in itself, grouped by style ---------- */

const GROUP_LABEL: Record<FontGroup, string> = { Clean: 'Clean', Rounded: 'Rounded', Condensed: 'Condensed', Digital: 'Digital & techy', Elegant: 'Elegant', Arabic: 'Arabic' };
function FontPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const cur = FONT_LIST.find((f) => f.name === value) ?? FONT_LIST[0]!;
  const groups = [...new Set(FONT_LIST.map((f) => f.group))];
  return (
    <div className="field"><span>Font</span>
      <button type="button" className="input font-btn" aria-expanded={open} onClick={() => setOpen((v) => !v)} style={{ fontFamily: cur.stack }}>
        <span>{cur.name}</span><Icon name="chevronDown" size={16} />
      </button>
      {open && (
        <div className="font-list" role="listbox" aria-label="Fonts">
          {groups.map((g) => (
            <div key={g} className="font-group">
              <div className="section-label">{GROUP_LABEL[g]}</div>
              {FONT_LIST.filter((f) => f.group === g).map((f) => (
                <button key={f.name} type="button" role="option" aria-selected={f.name === value} className={`font-opt${f.name === value ? ' on' : ''}`} onClick={() => { onChange(f.name); setOpen(false); }}>
                  <span className="fo-name" style={{ fontFamily: f.stack }}>{g === 'Arabic' ? 'السلام عليكم' : f.name}</span>
                  <span className="fo-sample" style={{ fontFamily: f.stack }}>{g === 'Arabic' ? f.name : '12:45'}</span>
                  {f.note && <small>{f.note}</small>}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Text options (Figma "Text options — expanded") ---------- */

function TextOptionsPanel({ w, onClose }: { w: Widget; onClose: () => void }) {
  const set = (patch: Partial<TextOptions>, key?: string) => useStudio.getState().updateWidget(w.id, (x) => ({ text: { ...x.text, ...patch } }), key);
  return (
    <div className="stack text-panel">
      <div className="sheet-head"><div style={{ flex: 1 }}><h2 style={{ fontSize: 26 }}>Text</h2><p>{WIDGETS[w.type].label} widget</p></div>
        <button type="button" className="icon-btn sm" aria-label="Close" onClick={onClose}><Icon name="x" size={18} /></button></div>
      <FontPicker value={w.text.font} onChange={(v) => set({ font: v })} />
      <div className="field"><span>Weight &amp; size</span>
        <div className="row">
          <Select label="Weight" style={{ flex: 1 }} value={String(w.text.weight)} onChange={(v) => set({ weight: Number(v) as TextOptions['weight'] })}
            options={[['400', 'Regular'], ['500', 'Medium'], ['600', 'Semibold'], ['700', 'Bold'], ['800', 'Heavy']].map(([v, l]) => ({ value: v!, label: l! }))} />
          {w.fit == null
            ? <PresetNumber label="Text size" unit="pt" value={w.text.size} min={MIN_TEXT} max={MAX_TEXT} presets={TEXT_SIZES} onChange={(v, k) => set({ size: v }, k === 'step' ? 'tsize' : undefined)} />
            : <span className="hint" style={{ maxWidth: 150 }}>Size is set by Content size</span>}
        </div>
      </div>
      {w.type !== 'makruh' && <div className="field"><span>Colour</span>
        <ColourChoice label="Text colour" autoLabel="Automatic (white or dark, whichever reads best)" swatches={TEXT_SWATCHES} value={w.textColor} keyPrefix="tc"
          onChange={(v, key) => useStudio.getState().updateWidget(w.id, { textColor: v }, key)} />
      </div>}
      <div className="field"><span>Alignment</span>
        <Segmented label="Alignment" value={w.text.align} onChange={(v) => set({ align: v })} options={[
          { value: 'left', label: <Icon name="alignLeft" size={20} label="Left" /> }, { value: 'center', label: <Icon name="alignCenter" size={20} label="Centre" /> }, { value: 'right', label: <Icon name="alignRight" size={20} label="Right" /> },
        ]} />
      </div>
      <div className="field"><span>Case</span>
        <Segmented label="Case" value={w.text.case} onChange={(v) => set({ case: v })} options={[{ value: 'normal', label: 'Aa' }, { value: 'upper', label: 'AA' }, { value: 'lower', label: 'aa' }]} />
      </div>
      {w.fit == null && <div className="toggle-row ctl-row"><span className="label">Scale text with widget</span><Switch label="Scale text with widget" checked={w.text.scale} onChange={(v) => set({ scale: v })} /></div>}
    </div>
  );
}

/* ---------- per-widget content ---------- */

function ContentEditor({ w, props }: { w: Widget; props: <P>(patch: Partial<P>, key?: string) => void }) {
  const ws = useApp((s) => s.ws)!;
  const design = useStudio((s) => s.design);
  const uses = (prop: string) => designUses(w, prop);
  switch (w.type) {
    case 'prayerTimes': {
      const p = w.props as PrayerTimesProps, set = props<PrayerTimesProps>;
      return (<>
        {isLed(w) && <LedColour value={p.digitColor} onChange={(v, key) => set({ digitColor: v }, key)} />}
        {uses('title') && <TextField label="Title" value={p.title} max={40} onChange={(v) => set({ title: v }, 'title')} />}
        {designOf(w) === 'tiles' && <SelectField label="Layout" value={p.layout} onChange={(v) => set({ layout: v })} options={[{ value: 'auto', label: 'Automatic' }, { value: 'row', label: 'Side by side' }, { value: 'column', label: 'Stacked list' }]} />}
        {(designOf(w) === 'icons' || designOf(w) === 'clocks') && <ToggleRow label="Rounded card around each prayer" checked={!!p.cards} onChange={(v) => set({ cards: v })} />}
        <ToggleRow label="Show sunrise" checked={p.showSunrise} onChange={(v) => set({ showSunrise: v })} />
        <ToggleRow label="Show jama‘ah times" checked={p.showJamaah} onChange={(v) => set({ showJamaah: v })} />
        <ToggleRow label="Add Tarawih in Ramadan" checked={p.showTarawih !== false} onChange={(v) => set({ showTarawih: v })} />
        {uses('showNext') && <ToggleRow label="Show time to next prayer" checked={p.showNext} onChange={(v) => set({ showNext: v })} />}
        <ToggleRow label="12-hour times" checked={p.h12} onChange={(v) => set({ h12: v })} />
        {uses('tiles') && <PrayerTileColours w={w} p={p} />}
        <p className="hint">Times come from your uploaded timetable. On Fridays, Dhuhr shows as Jumu‘ah with both jama‘ahs. <button type="button" className="link" onClick={() => openSheet({ name: 'csv' })}>Upload a timetable</button></p>
      </>);
    }
    case 'clock': {
      const p = w.props as ClockProps, set = props<ClockProps>;
      return (<>
        {isLed(w) && <LedColour value={p.digitColor} onChange={(v, key) => set({ digitColor: v }, key)} />}
        <ToggleRow label="Show seconds" checked={p.showSeconds} onChange={(v) => set({ showSeconds: v })} />
        <ToggleRow label="Show date" checked={p.showDate} onChange={(v) => set({ showDate: v })} />
        <ToggleRow label="Show Hijri date" checked={p.showHijri} onChange={(v) => set({ showHijri: v })} />
        {uses('h12') && <ToggleRow label="12-hour clock" checked={p.h12} onChange={(v) => set({ h12: v })} />}
      </>);
    }
    case 'jumuah': {
      const p = w.props as JumuahProps, set = props<JumuahProps>;
      return (<>
        <TextField label="Title" value={p.title} max={30} onChange={(v) => set({ title: v }, 'title')} />
        <p className="hint">Jumu‘ah 1 and 2 come from your timetable each Friday; khutbah times are in Settings. <button type="button" className="link" onClick={() => openSheet({ name: 'jumuah' })}>Edit khutbah times</button></p>
      </>);
    }
    case 'countdown': {
      const p = w.props as CountdownProps, set = props<CountdownProps>;
      return (<>
        {isLed(w) && <LedColour value={p.digitColor} onChange={(v, key) => set({ digitColor: v }, key)} />}
        <SelectField label="Count down to" value={p.target} onChange={(v) => set({ target: v })} options={[{ value: 'nextJamaah', label: 'Next jama‘ah' }, { value: 'nextStart', label: 'Next prayer start' }, { value: 'custom', label: 'A date and time' }]} />
        {p.target === 'custom' && <>
          <TextField label="Label" value={p.label} max={40} onChange={(v) => set({ label: v }, 'label')} placeholder="e.g. Eid salah in" />
          <TextField label="Date and time" type="datetime-local" value={(p.customAt ?? '').slice(0, 16)} onChange={(v) => set({ customAt: v ? new Date(v).toISOString() : '' })} />
        </>}
      </>);
    }
    case 'announcements': {
      const p = w.props as AnnouncementsProps, set = props<AnnouncementsProps>;
      if (designOf(w) === 'alert') {
        const a = p.alert ?? { preset: 'notice' as const, heading: 'Notice', body: '' };
        // Choosing a kind gives the message that kind's colour; the Colour setting above can then change it.
        const pick = (id: MessagePreset) => {
          const cur = presetOf(a.preset), next = presetOf(id);
          const alert = { preset: id, heading: !a.heading || a.heading === cur.heading ? next.heading : a.heading, body: !a.body || a.body === cur.body ? next.body : a.body };
          useStudio.getState().updateWidget(w.id, (x) => ({ props: { ...x.props, alert }, fill: null }));
        };
        return (<>
          <div className="bc-presets sm" role="radiogroup" aria-label="Kind of message">
            {MESSAGE_PRESETS.map((x) => <button key={x.id} type="button" role="radio" aria-checked={a.preset === x.id} className={`bc-preset${a.preset === x.id ? ' on' : ''}`} style={{ ['--bm' as string]: x.color }} onClick={() => pick(x.id)}>{x.label}</button>)}
          </div>
          <TextField label="Heading" value={a.heading} max={60} onChange={(v) => set({ alert: { ...a, heading: v } }, 'alert-h')} />
          <AreaField label="Message" value={a.body} max={240} onChange={(v) => set({ alert: { ...a, body: v } }, 'alert-b')} />
          <p className="hint">This stays in the design. To put a message on screens straight away, for a set time, send it from the dashboard. <button type="button" className="link" onClick={() => openSheet({ name: 'message' })}>Send an urgent message</button></p>
        </>);
      }
      return (<>
        <TextField label="Title" value={p.title} max={40} onChange={(v) => set({ title: v }, 'title')} />
        {uses('seconds') && <NumberField label="Seconds per announcement" value={p.seconds} min={3} max={120} onChange={(v) => set({ seconds: v }, 'secs')} />}
        <ListEditor label="Announcements" items={p.items} max={20} addLabel="Add announcement" title={(a) => a.title} onChange={(items) => set({ items }, 'items')}
          make={() => ({ id: newId('a'), title: 'New announcement', body: '' })}
          render={(a, s) => (<><TextField label="Heading" value={a.title} max={80} onChange={(v) => s({ title: v })} /><AreaField label="Details" value={a.body} max={400} onChange={(v) => s({ body: v })} /></>)} />
      </>);
    }
    case 'posters': {
      const p = w.props as PostersProps, set = props<PostersProps>;
      const design = useStudio.getState().design!;
      const setShape = (shape: PosterShape) => useStudio.getState().updateWidget(w.id, (x) => {
        const nx = { ...x, props: { ...x.props, shape } };
        return { props: nx.props, ...keepShape(nx, { x: x.x, y: x.y, w: x.w, h: x.h }, design, 'h') };
      });
      const added = async (urls: string[]) => {
        // The first poster sets the shape, so the widget matches what was uploaded.
        if (!p.items.length && urls[0]) {
          const r = await imageRatio(urls[0]).catch(() => null);
          if (r) setShape(nearestPosterShape(r));
        }
        set({ items: [...useStudio.getState().design!.widgets.find((x) => x.id === w.id)!.props.items as PostersProps['items'], ...urls.map((src) => ({ id: newId('p'), src }))].slice(0, 30) });
      };
      return (<>
        <SelectField label="Poster shape" value={p.shape ?? 'a-portrait'} onChange={(v) => setShape(v)} options={POSTER_SHAPES.map((x) => ({ value: x.value, label: `${x.label} · ${x.detail}` }))} />
        <div className="posters-ed">
          {p.items.map((it, i) => (
            <div key={it.id} className="pthumb">
              <img src={it.src} alt={it.alt ?? ''} />
              <button type="button" className="lbtn danger" aria-label={`Remove poster ${i + 1}`} onClick={() => set({ items: p.items.filter((x) => x.id !== it.id) })}><Icon name="x" size={14} /></button>
            </div>
          ))}
        </div>
        <UploadButton multiple label={p.items.length ? 'Add more posters' : 'Upload posters'} onUploaded={(urls) => void added(urls)} />
        {designOf(w) === 'flyin' ? <>
          <div className="field"><span>How it comes and goes</span>
            <Segmented size="sm" label="How it comes and goes" value={p.transition} onChange={(v) => set({ transition: v })} options={[{ value: 'fade', label: 'Fade' }, { value: 'slide', label: 'Slide' }]} />
          </div>
          {p.transition === 'slide' && <div className="field"><span>Slides in from</span>
            <Segmented size="sm" label="Slides in from" value={p.enter ?? 'right'} onChange={(v) => set({ enter: v })} options={[{ value: 'left', label: 'Left' }, { value: 'right', label: 'Right' }, { value: 'top', label: 'Top' }, { value: 'bottom', label: 'Bottom' }]} />
            <small className="hint">It carries on and leaves through the {({ left: 'right', right: 'left', top: 'bottom', bottom: 'top' } as const)[p.enter ?? 'right']} edge.</small>
          </div>}
          <SelectField label="Speed" value={p.speed ?? 'medium'} onChange={(v) => set({ speed: v as SlideSpeed })} options={SLIDE_SPEEDS.map((x) => ({ value: x.value, label: x.label }))} />
          <div className="two">
            <NumberField label="Shows for (seconds)" value={p.seconds} min={3} max={600} onChange={(v) => set({ seconds: v }, 'secs')} />
            <NumberField label="Break before the next (seconds)" value={p.gap ?? 15} min={0} max={600} onChange={(v) => set({ gap: v }, 'gap')} />
          </div>
          <p className="hint" style={{ margin: 0 }}>{p.transition === 'slide' ? 'Each poster slides in from the edge of the screen to its place, stays, then slides back off.' : 'Each poster fades in where it is, stays, then fades away.'} During the break, whatever is underneath shows. It moves on the screens and in Preview; here it stays still so you can edit it.</p>
        </> : <NumberField label="Seconds per poster" value={p.seconds} min={3} max={300} onChange={(v) => set({ seconds: v }, 'secs')} />}
        <Segmented size="sm" label="Fit" value={p.fit} onChange={(v) => set({ fit: v })} options={[{ value: 'contain', label: 'Show whole' }, { value: 'cover', label: 'Fill' }]} />
        {designOf(w) !== 'flyin' && <Segmented size="sm" label="Transition" value={p.transition} onChange={(v) => set({ transition: v })} options={[{ value: 'fade', label: 'Fade' }, { value: 'slide', label: 'Slide' }]} />}
      </>);
    }
    case 'makruh': {
      const p = w.props as MakruhProps, set = props<MakruhProps>;
      const hasTicker = !!design?.widgets.some((x) => x.type === 'ticker' && !x.hidden);
      const today = makruhPeriods(ws.masjid, indexTimetable(ws.timetable), ymd(new Date(), ws.masjid.timezone), p);
      const at = (d: Date) => hhmm(d, ws.masjid.timezone);
      return (<>
        <p className="hint" style={{ margin: 0 }}>Shows on the screen only during makruh times, as a red banner: “Please do not pray any salah”.</p>
        <NumberField label="After sunrise (minutes)" value={p.afterSunrise} min={MAKRUH_LIMITS.afterSunrise[0]} max={MAKRUH_LIMITS.afterSunrise[1]} onChange={(v) => set({ afterSunrise: v }, 'sun')} />
        <NumberField label="Zawal, before Dhuhr (minutes)" value={p.beforeDhuhr} min={MAKRUH_LIMITS.beforeDhuhr[0]} max={MAKRUH_LIMITS.beforeDhuhr[1]} onChange={(v) => set({ beforeDhuhr: v }, 'zaw')} />
        <Segmented size="sm" label="Where" value={p.place} onChange={(v) => set({ place: v })} options={[{ value: 'ticker', label: 'Over the news ticker' }, { value: 'own', label: 'Its own place' }]} />
        <p className="hint" style={{ margin: 0 }}>{p.place === 'own' ? 'Shows where you’ve placed it.' : hasTicker ? 'Takes the news ticker’s place while it shows.' : 'There’s no news ticker on this screen, so it shows where you’ve placed it.'}</p>
        {today.length > 0 && <p className="hint" style={{ margin: 0 }}>Today: {today.map((x) => `${x.kind === 'sunrise' ? 'after sunrise' : 'zawal'} ${at(x.from)}–${at(x.until)}`).join(' · ')}</p>}
      </>);
    }
    case 'heading': {
      const p = w.props as HeadingProps, set = props<HeadingProps>;
      return <TextField label="Heading" value={p.text} max={HEADING_MAX} counter placeholder="e.g. Welcome to our masjid" onChange={(v) => set({ text: v }, 'text')} />;
    }
    case 'paragraph': {
      const p = w.props as ParagraphProps, set = props<ParagraphProps>;
      return (<>
        <TextField label="Title (optional)" value={p.title} max={PARAGRAPH_TITLE_MAX} counter onChange={(v) => set({ title: v }, 'title')} />
        <AreaField label="Text" value={p.text} max={PARAGRAPH_MAX} rows={6} counter onChange={(v) => set({ text: v }, 'text')} />
      </>);
    }
    case 'logo': {
      const p = w.props as LogoProps, set = props<LogoProps>;
      return (<>
        {ws.masjid.logo ? <div className="logo-prev ctl"><img src={ws.masjid.logo} alt={`${ws.masjid.name} logo`} /></div> : <p className="hint" style={{ margin: 0 }}>No logo yet.</p>}
        <button type="button" className="btn sm plain" onClick={() => openSheet({ name: 'logo' })}><Icon name="upload" size={16} />{ws.masjid.logo ? 'Change logo' : 'Add your logo'}</button>
        <Segmented size="sm" label="Fit" value={p.fit} onChange={(v) => set({ fit: v })} options={[{ value: 'contain', label: 'Show whole' }, { value: 'cover', label: 'Fill' }]} />
        <p className="hint" style={{ margin: 0 }}>The same logo is used on every screen. Set the colour’s opacity to 0% for no background behind it.</p>
      </>);
    }
    case 'donations': {
      const p = w.props as DonationsProps, set = props<DonationsProps>;
      return (<>
        <TextField label="Appeal" value={p.appeal} max={60} placeholder="e.g. Masjid extension" onChange={(v) => set({ appeal: v }, 'appeal')} />
        <div className="two"><NumberField label="Raised" value={p.raised} min={0} onChange={(v) => set({ raised: v }, 'raised')} /><NumberField label="Target" value={p.goal} min={0} onChange={(v) => set({ goal: v }, 'goal')} /></div>
        <TextField label="Currency symbol" value={p.currency} max={3} onChange={(v) => set({ currency: v }, 'cur')} />
        <ToggleRow label="Percentage" checked={p.showPercent !== false} onChange={(v) => set({ showPercent: v })} />
        <ToggleRow label="Still to raise" checked={p.showRemaining !== false} onChange={(v) => set({ showRemaining: v })} />
        <ToggleRow label="Number of donors" checked={!!p.showDonors} onChange={(v) => set({ showDonors: v })} />
        {p.showDonors && <NumberField label="Donors" value={p.donors ?? 0} min={0} onChange={(v) => set({ donors: v }, 'donors')} />}
      </>);
    }
    case 'donationQr': {
      const p = w.props as DonationQrProps, set = props<DonationQrProps>;
      const bad = !!p.url && !/^https?:\/\/\S+\.\S+/i.test(p.url);
      return (<>
        {uses('title') && <TextField label="Title" value={p.title} max={30} onChange={(v) => set({ title: v }, 'title')} />}
        <TextField label="Donation link" type="url" value={p.url} max={500} placeholder="https://" onChange={(v) => set({ url: v.trim() }, 'url')} hint={bad ? 'Enter a full web address starting with https://' : 'The QR code opens this page on people’s phones.'} />
        {uses('caption') && <TextField label="Caption" value={p.caption} max={60} onChange={(v) => set({ caption: v }, 'cap')} />}
      </>);
    }
    case 'ayah': case 'hadith': {
      const p = w.props as ScriptureProps, set = props<ScriptureProps>;
      const ayah = w.type === 'ayah';
      const today = ymd(new Date(), ws.masjid.timezone);
      const now = p.mode !== 'custom' ? scriptureFor(w.type, p, today) : null;
      const sources = ayah ? [{ value: 'all', label: 'All themes' }, ...AYAH_TOPICS.map((t) => ({ value: t.id, label: t.name }))]
        : [{ value: 'all', label: 'All collections' }, ...HADITH_BOOKS.map((b) => ({ value: b.id, label: b.name }))];
      const items = ayah ? QURAN_AYAT.map((a) => ({ value: a.id, label: `${a.surahName} ${a.verses}: ${a.text.slice(0, 64)}${a.text.length > 64 ? '…' : ''}` }))
        : HADITH_LIST.filter((h) => !p.source || p.source === 'all' || h.book === p.source).map((h) => ({ value: h.id, label: `${hadithBookName(h.book)} ${h.number}: ${h.text.slice(0, 60)}${h.text.length > 60 ? '…' : ''}` }));
      const link = !ayah && now ? (() => { const h = HADITH_LIST.find((x) => hadithReference(x) === now.reference); return h ? HADITH_BOOKS.find((b) => b.id === h.book)!.url(h.number) : null; })()
        : ayah && now ? (() => { const a = QURAN_AYAT.find((x) => ayahReference(x) === now.reference); return a ? `https://quran.com/${a.surah}/${a.verses.split(/[–-]/)[0]!.split(':')[1]}` : null; })() : null;
      return (<>
        {uses('title') && <TextField label="Title" value={p.title} max={30} onChange={(v) => set({ title: v }, 'title')} />}
        <Segmented size="sm" label="Source" value={p.mode} onChange={(v) => set({ mode: v, ...(v === 'pick' && !p.pick ? { pick: items[0]?.value } : {}) })} options={[{ value: 'daily', label: 'New each day' }, { value: 'pick', label: 'Choose one' }, { value: 'custom', label: 'My own' }]} />
        {p.mode !== 'custom' && <SelectField label={ayah ? 'Theme' : 'Collection'} value={p.source || 'all'} onChange={(v) => set({ source: v === 'all' ? undefined : v })} options={sources} />}
        {p.mode === 'pick' && <SelectField label={ayah ? 'Ayah' : 'Hadith'} value={p.pick && items.some((i) => i.value === p.pick) ? p.pick : items[0]?.value ?? ''} onChange={(v) => set({ pick: v })} options={items} />}
        {now && <div className="scr-now ctl"><p>{now.text}</p><small>{[!ayah ? now.narrator : '', now.reference].filter(Boolean).join(' · ')}</small>
          {link && <a className="link" href={link} target="_blank" rel="noreferrer noopener">{ayah ? 'Read it on quran.com' : 'Read the full hadith on sunnah.com'}</a>}</div>}
        {p.mode !== 'custom' && (ayah
          ? (designUses(w, 'showArabic') ? <ToggleRow label="Show the Arabic" checked={p.showArabic !== false} onChange={(v) => set({ showArabic: v })} /> : null)
          : <ToggleRow label="Show the narrator" checked={p.showNarrator !== false} onChange={(v) => set({ showNarrator: v })} />)}
        {p.mode !== 'custom' && <p className="hint">{ayah ? 'Arabic from Tanzil; meaning from Saheeh International.' : 'From sunnah.com, word for word, with its grading. Only sahih and hasan hadith are included.'}</p>}
        {p.mode === 'custom' && <>
          {ayah && <AreaField label="Arabic (optional)" dir="rtl" value={p.arabic ?? ''} max={600} onChange={(v) => set({ arabic: v }, 'ar')} />}
          <AreaField label="Text" value={p.text} max={600} onChange={(v) => set({ text: v }, 'text')} />
          <TextField label="Reference" value={p.reference} max={80} onChange={(v) => set({ reference: v }, 'ref')} />
        </>}
      </>);
    }
    case 'events': {
      const p = w.props as EventsProps, set = props<EventsProps>;
      return (<>
        <TextField label="Title" value={p.title} max={30} onChange={(v) => set({ title: v }, 'title')} />
        <NumberField label="Show up to" value={p.max} min={1} max={10} suffix="events" onChange={(v) => set({ max: v }, 'max')} />
        <ListEditor label="Events" items={p.items} max={40} addLabel="Add event" title={(e) => `${e.title}${e.date ? ` · ${e.date}` : ''}`} onChange={(items) => set({ items }, 'items')}
          make={(): EventItem => ({ id: newId('e'), title: 'New event', date: new Date().toISOString().slice(0, 10) })}
          render={(e, s) => (<>
            <TextField label="Name" value={e.title} max={60} onChange={(v) => s({ title: v })} />
            <div className="two"><TextField label="Date" type="date" value={e.date} onChange={(v) => s({ date: v })} /><TextField label="Time" type="time" value={e.time ?? ''} onChange={(v) => s({ time: v })} /></div>
            <TextField label="Place" value={e.place ?? ''} max={60} onChange={(v) => s({ place: v })} />
          </>)} />
        <p className="hint">Past events disappear from the screen automatically.</p>
      </>);
    }
    case 'iftar': case 'sehri': case 'tarawih': {
      const p = w.props as RamadanProps, set = props<RamadanProps>;
      const col = w.type === 'iftar' ? 'Maghrib Begins' : w.type === 'sehri' ? 'Sehri Ends' : 'Tarawih';
      return (<>
        <TextField label="Title" value={p.title} max={30} onChange={(v) => set({ title: v }, 'title')} />
        {uses('showCountdown') && <ToggleRow label="Show time left" checked={p.showCountdown} onChange={(v) => set({ showCountdown: v })} />}
        <ToggleRow label="Show the day of Ramadan" checked={p.showDay} onChange={(v) => set({ showDay: v })} />
        <ToggleRow label="12-hour time" checked={p.h12} onChange={(v) => set({ h12: v })} />
        <p className="hint">Times come from the {col} column of your timetable.{w.type !== 'iftar' ? ' Fill it in for Ramadan and upload the CSV again.' : ''} <button type="button" className="link" onClick={() => openSheet({ name: 'csv' })}>Upload a timetable</button></p>
      </>);
    }
    case 'ticker': {
      const p = w.props as TickerProps, set = props<TickerProps>;
      return (<>
        {uses('tag') && <TextField label="Tag" value={p.tag} max={12} onChange={(v) => set({ tag: v }, 'tag')} />}
        <div className="field"><span>Scrolling speed</span>
          <Slider label="Scrolling speed" value={p.speed} min={10} max={200} step={5} onChange={(v) => set({ speed: v }, 'speed')} format={speedName} ends={['Slow', 'Fast']} />
        </div>
        <ListEditor label="Lines" items={p.items} max={20} addLabel="Add line" title={(i) => i.text} onChange={(items) => set({ items }, 'items')}
          make={() => ({ id: newId('t'), text: 'New line' })} render={(i, s) => <AreaField label="Text" rows={2} value={i.text} max={200} onChange={(v) => s({ text: v })} />} />
      </>);
    }
  }
}

/* ---------- nothing selected: the screen itself ---------- */

const BG_PRESETS = [['gradient', '#0B1626', '#02060D'], ['gradient', '#0A84FF', '#020A1C'], ['gradient', '#0E5E4E', '#02130F'], ['gradient', '#3A2A12', '#0B0703'], ['gradient', '#2A0F4C', '#0B0418'], ['solid', '#000000'], ['solid', '#0A3D91'], ['solid', '#0A84FF'], ['solid', '#FFFFFF'], ['solid', '#F4F1EA']] as const;
const BG_SWATCHES = BACKGROUND_SWATCHES;
const PATTERN_SWATCHES = ['#FFFFFF', '#E7C35A', '#D4AF37', '#0A84FF', '#30D158', '#FF9F0A', '#0B1220'] as const;

/** Islamic and Arabic-inspired patterns over the background. */
function PatternEditor() {
  const design = useStudio((s) => s.design)!;
  const change = useStudio((s) => s.change);
  const p = design.background.pattern ?? null;
  const set = (patch: Partial<BackgroundPattern> | null, key?: string) => change((d) => ({
    ...d, background: { ...d.background, pattern: patch === null ? null : { ...(d.background.pattern ?? { id: 'khatam', color: isLightBackground(d.background) ? '#0B1220' : '#FFFFFF', opacity: 14, coverage: 'full', size: 'medium' }), ...patch } as BackgroundPattern },
  }), key ? `pat:${key}` : undefined);
  const light = isLightBackground(design.background);
  return (
    <div className="insp-sec">
      <div className="section-label">Pattern</div>
      <div className="patterns" role="radiogroup" aria-label="Pattern">
        <button type="button" role="radio" aria-checked={!p} className={`pat-opt none${!p ? ' on' : ''}`} onClick={() => set(null)}><span className="pat-thumb"><Icon name="x" size={18} /></span><span>None</span></button>
        {PATTERNS.map((x) => (
          <button key={x.id} type="button" role="radio" aria-checked={p?.id === x.id} className={`pat-opt${p?.id === x.id ? ' on' : ''}`} title={x.label} onClick={() => set({ id: x.id })}>
            <img className="pat-thumb" alt="" src={`data:image/svg+xml;utf8,${encodeURIComponent(patternPreviewSvg(x.id, light ? '#0B1220' : '#FFFFFF', light ? '#F4F1EA' : '#0B1626'))}`} />
            <span>{x.label}</span>
          </button>
        ))}
      </div>
      {p && <>
        <div className="field"><span>Where</span>
          <Segmented size="sm" label="Where" value={p.coverage} onChange={(v: PatternCoverage) => set({ coverage: v })} options={[{ value: 'full', label: 'Full screen' }, { value: 'border', label: 'Border' }, { value: 'corners', label: 'Corners' }]} />
        </div>
        <div className="field"><span>Size</span>
          <Segmented size="sm" label="Pattern size" value={p.size} onChange={(v: PatternSize) => set({ size: v })} options={[{ value: 'small', label: 'Small' }, { value: 'medium', label: 'Medium' }, { value: 'large', label: 'Large' }]} />
        </div>
        <div className="field"><span>Visibility</span>
          <Slider label="Pattern visibility" value={p.opacity} min={2} max={60} onChange={(v) => set({ opacity: v }, 'opacity')} format={(v) => `${v}%`} ends={['Subtle', 'Bold']} />
        </div>
        <div className="field"><span>Pattern colour</span>
          <ColourChoice label="Pattern colour" swatches={PATTERN_SWATCHES} value={p.color} keyPrefix="pc" onChange={(v, k) => set({ color: v ?? '#FFFFFF' }, `c${k ?? ''}`)} />
        </div>
      </>}
    </div>
  );
}

function ScreenInspector() {
  const design = useStudio((s) => s.design)!;
  const change = useStudio((s) => s.change);
  const bg = design.background;
  const setBg = (patch: Partial<Design['background']>, key?: string) => change((d) => ({ ...d, background: { ...d.background, ...patch } }), key ? `bg:${key}` : undefined);
  return (
    <div className="insp">
      <div className="insp-head">
        <span className="insp-ic"><Icon name="monitor" size={26} /></span>
        <div className="insp-name"><b>Screen</b><small>{design.widgets.length} widget{design.widgets.length === 1 ? '' : 's'} · {design.cols} × {design.rows} grid</small></div>
      </div>
      <div className="sep" />
      <div className="insp-sec">
        <div className="section-label">Background</div>
        <p className="hint" style={{ margin: '-4px 0 0' }}>The whole screen, behind every widget.</p>
        <Segmented size="sm" label="Background" value={bg.kind} onChange={(v) => setBg({ kind: v })} options={[{ value: 'gradient', label: 'Gradient' }, { value: 'solid', label: 'Solid' }, { value: 'image', label: 'Image' }]} />
        <div className="presets" role="group" aria-label="Background presets">
          {BG_PRESETS.map(([k, c1, c2]) => (
            <button key={`${k}${c1}${c2}`} type="button" className={`sw${bg.kind === k && bg.color === c1 && (k === 'solid' || bg.color2 === c2) ? ' on' : ''}`} aria-label={`${k === 'gradient' ? 'Gradient' : 'Solid'} background ${c1}`} title={`${k === 'gradient' ? 'Gradient' : 'Solid'} ${c1}`} style={{ background: k === 'gradient' ? `radial-gradient(120% 120% at 80% -10%, ${c1}, ${c2} 70%)` : c1 }}
              onClick={() => change((d) => ({ ...d, background: { ...d.background, kind: k as 'solid' | 'gradient', color: c1!, ...(c2 ? { color2: c2 } : {}) } }))} />
          ))}
        </div>
        <div className="field"><span>{bg.kind === 'gradient' ? 'Glow colour' : 'Colour'}</span>
          <ColourChoice label={bg.kind === 'gradient' ? 'Glow colour' : 'Background colour'} swatches={BG_SWATCHES} value={bg.color} keyPrefix="bg1" onChange={(v, k) => setBg({ color: v ?? '#000000' }, `c1${k ?? ''}`)} />
        </div>
        {bg.kind === 'gradient' && <div className="field"><span>Base colour</span>
          <ColourChoice label="Base colour" swatches={BG_SWATCHES} value={bg.color2 ?? '#000000'} keyPrefix="bg2" onChange={(v, k) => setBg({ color2: v ?? '#000000' }, `c2${k ?? ''}`)} />
        </div>}
        {bg.kind === 'image' && (<>
          {bg.image && <div className="pthumb wide"><img src={bg.image} alt="" /></div>}
          <UploadButton label={bg.image ? 'Replace image' : 'Upload background'} onUploaded={([u]) => u && setBg({ image: u })} />
        </>)}
      </div>
      <div className="sep" />
      <PatternEditor />
      <div className="sep" />
      <div className="empty" style={{ padding: '8px 0' }}>
        <Icon name="hand" size={28} />
        <span>Select a widget to change its size, style, colour, text and content.</span>
        {!design.widgets.length && <button type="button" className="btn sm plain" onClick={async () => {
          if (design.widgets.length && !(await confirm('Replace the canvas?'))) return;
          const w = createWidget('prayerTimes', placeNew(design.widgets, 'prayerTimes', design.cols, design.rows));
          change((d) => ({ ...d, widgets: [...d.widgets, w] }));
          useStudio.getState().select(w.id);
        }}><Icon name="plus" size={16} />Add Prayer Times</button>}
      </div>
    </div>
  );
}

/* ---------- Live on screen ---------- */

/**
 * What a screen is showing now. A slim bar under the inspector (so the inspector has the room); open it to see the
 * live screen. The arrows (or a swipe) go through every screen with something live, starting at the one being edited
 * (from the menu, with no screen, at the first). Whether it's open is remembered.
 */
export function LiveOnScreen({ screenId }: { screenId?: string }) {
  const ws = useApp((s) => s.ws)!;
  const now = useApp((s) => s.now);
  const list = ws.screens.filter((s) => s.id === screenId || !!liveDesign(ws, s.id, now));
  const [pick, setPick] = useState<string | undefined>(screenId);
  useEffect(() => { setPick(screenId); }, [screenId]);
  const i = Math.max(0, list.findIndex((s) => s.id === pick));
  const screen = list[i];
  const bcs = useScreenBroadcasts(screen?.id);
  const d = screen ? liveDesign(ws, screen.id, now) : null;
  const st = screen ? screenStatus(screen, now) : 'unpaired';
  const live = st === 'online' || st === 'idle' || st === 'off';
  const [open, setOpen] = useState(() => { try { return localStorage.getItem('noorview.studio.live') === '1'; } catch { return false; } });
  // Once opened, the preview stays built, so closing it animates instead of vanishing.
  const [seen, setSeen] = useState(open);
  const toggle = () => { setSeen(true); setOpen((v) => { try { localStorage.setItem('noorview.studio.live', v ? '0' : '1'); } catch { /* ignore */ } return !v; }); };
  const go = (step: number) => { if (list.length > 1) setPick(list[(i + step + list.length) % list.length]!.id); };
  // A sideways swipe on the picture goes to the next or previous screen (and isn't taken as a tap to open it).
  const swipe = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  return (
    <section className={`card glass live-mini${open ? ' open' : ''}`} aria-label="Live on screen">
      <div className="card-head">
        <button type="button" className="live-title" onClick={toggle} aria-hidden tabIndex={-1}><b className="t17">Live on screen</b></button>
        <span className="spacer" />
        <button type="button" className="icon-btn sm live-toggle" aria-expanded={open} aria-label={open ? 'Hide the live screen' : 'Show the live screen'} title={open ? 'Hide the live screen' : 'Show the live screen'} onClick={toggle}><Icon name="chevronDown" size={18} /></button>
      </div>
      <div className="live-body" inert={!open || undefined}>
        <div>
          {screen && (
            // The screen's name, status and place in the list under the title, with arrows either side when there's more than one.
            <div className="live-nav">
              {list.length > 1 && <button type="button" className="icon-btn sm" aria-label="Previous screen" title="Previous screen" onClick={() => go(-1)}><Icon name="chevron" size={16} className="flip" /></button>}
              <span className="live-name" aria-live="polite"><b>{screen.name}</b>
                <span className="live-sub"><span className={`pill ${live ? 'online' : STATUS_PILL[st]}`}>{live ? 'Live' : STATUS_LABEL[st]}</span>{list.length > 1 && <small>{i + 1} of {list.length}</small>}</span>
              </span>
              {list.length > 1 && <button type="button" className="icon-btn sm" aria-label="Next screen" title="Next screen" onClick={() => go(1)}><Icon name="chevron" size={16} /></button>}
            </div>
          )}
          <button type="button" className="mini-frame" disabled={!screen} aria-label={screen ? `Open ${screen.name} full size` : 'No screens yet'}
            onClick={() => { if (swipe.current?.moved) return; if (screen) openSheet({ name: 'fullscreen', screenId: screen.id }); }}
            onPointerDown={(e) => { swipe.current = { x: e.clientX, y: e.clientY, moved: false }; }}
            onPointerUp={(e) => { const s0 = swipe.current; if (!s0) return; const dx = e.clientX - s0.x; if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(e.clientY - s0.y)) { s0.moved = true; go(dx < 0 ? 1 : -1); } }}
            style={{ touchAction: list.length > 1 ? 'pan-y' : undefined }}>
            {seen && (screen && d ? <StageView key={screen.id} design={d} masjid={ws.masjid} timetable={ws.timetable} power={screen.power} salahBlank={screen.salahBlank !== false} broadcasts={bcs} className="stage-fill" label={`What ${screen.name} is showing now`} /> : <span className="hint">Nothing live yet</span>)}
          </button>
          {screen && !screen.deviceId && <button type="button" className="btn sm plain block" onClick={() => openSheet({ name: 'pair', screenId: screen.id })}><Icon name="tv" size={16} />Pair {list.length > 1 ? screen.name : 'a display'}</button>}
        </div>
      </div>
    </section>
  );
}

const speedName = (v: number) => (v <= 30 ? 'Very slow' : v <= 50 ? 'Slow' : v <= 80 ? 'Normal' : v <= 130 ? 'Fast' : 'Very fast');

function imageRatio(src: string): Promise<number> {
  return new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i.naturalWidth / Math.max(1, i.naturalHeight)); i.onerror = rej; i.src = src; });
}

/** The Digital LED design's digit colour (red by default). */
function LedColour({ value, onChange }: { value: string | null | undefined; onChange: (v: string, key?: string) => void }) {
  return (
    <div className="field"><span>Digit colour</span>
      <ColourChoice label="Digit colour" swatches={LED_COLOURS} value={value ?? LED_DEFAULT} keyPrefix="digit" onChange={(v, key) => onChange(v ?? LED_DEFAULT, key)} />
    </div>
  );
}
